import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Enrollment } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';


export default function EnrollmentList() {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getEnrollments();
      
      if (response.success && response.data) {
        setEnrollments(response.data?.results);
      } else {
        setError(response.error || 'Failed to load enrollments');
      }
    } catch (err) {
      setError('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async (enrollmentId: number) => {
    if (!confirm('Are you sure you want to unenroll from this course?')) return;
    
    try {
      const response = await apiClient.deleteEnrollment(enrollmentId);
      if (response.success) {
        setEnrollments(enrollments.filter(enrollment => enrollment.id !== enrollmentId));
      } else {
        alert('Failed to unenroll from course');
      }
    } catch (err) {
      alert('Failed to unenroll from course');
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 60) return 'text-yellow-600';
    if (progress >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadEnrollments}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">My Enrollments</h2>
        <Link
          to="/courses"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          Browse More Courses
        </Link>
      </div>

      {enrollments.length === 0 ? (
        <EmptyState
          icon="📖"
          title="You haven't enrolled in any courses yet."
          description="Browse available courses and enroll to start learning."
          action={
            <Link
              to="/courses"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium"
            >
              Browse Available Courses
            </Link>
          }
          hintsTitle="Next steps:"
          hints={[
            { icon: '📚', label: 'Browse the course catalog' },
            { icon: '✅', label: 'Enroll in a course' },
            { icon: '📈', label: 'Track your progress as you learn' },
          ]}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrollments && enrollments?.map((enrollment) => (
            <div key={enrollment.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {enrollment.course.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {enrollment.course.description}
                </p>
                
                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <p><strong>Tutor:</strong> {enrollment.course?.tutor?.first_name} {enrollment.course?.tutor?.last_name}</p>
                  <p><strong>Enrolled:</strong> {new Date(enrollment.enrolled_at).toLocaleDateString()}</p>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className={`text-sm font-medium ${getProgressColor(enrollment.progress)}`}>
                      {enrollment.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${enrollment.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Progress Status */}
                <div className="mb-4">
                  {enrollment.progress === 0 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Not Started
                    </span>
                  )}
                  {enrollment.progress > 0 && enrollment.progress < 100 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      In Progress
                    </span>
                  )}
                  {enrollment.progress === 100 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Completed
                    </span>
                  )}
                </div>
                
                <div className="flex space-x-2">
                  <Link
                    to={`/courses/${enrollment.course.id}`}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-md text-sm font-medium"
                  >
                    Continue Learning
                  </Link>
                  
                  <button
                    onClick={() => handleUnenroll(enrollment.id)}
                    className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md text-sm font-medium"
                  >
                    Unenroll
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 